import { Children, useLayoutEffect, useRef } from 'react';
import { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import CustomScrollbar from 'components/Common/CustomScrollbar';

const ScrolledValueContainer = ({ children, selectProps }) => {
  const scrollRef = useRef(null);
  const childArray = Children.toArray(children);
  const hasValue = !!selectProps.value?.length;
  const values = hasValue ? childArray.slice(0, -1) : [];
  const input = childArray[childArray.length - 1];

  useLayoutEffect(() => {
    scrollRef.current?.scrollToBottom();
  });

  return (
    <div className='d-flex flex-column' style={{ flex: 1, minWidth: 0, height: 176 }}>
      <div className='interaction-contacts-select__input-row'>
        <div className='interaction-contacts-select__input-inner d-flex align-items-center gap-2'>
          <span
            className='flex-shrink-0 user-select-none text-main-blue-35 title-h3 text-center'
            style={{ minWidth: 36 }}
          >
            +
          </span>
          <div className='d-flex align-items-center position-relative' style={{ flex: 1 }}>
            {!selectProps.inputValue && (
              <span className='interaction-contacts-select__placeholder-text position-absolute'>
                {selectProps.placeholder}
              </span>
            )}
            {input}
          </div>
        </div>
      </div>
      <CustomScrollbar
        ref={scrollRef}
        className='simple-scrollbar'
        style={{ flex: 1, minHeight: 0 }}
        maximalThumbYSize={50}
        minimalThumbYSize={50}
        disabled={selectProps.menuIsOpen}
      >
        <div className='d-flex flex-column align-items-stretch' style={{ gap: 2, padding: hasValue ? '0 8px 4px' : 0 }}>
          {values}
        </div>
      </CustomScrollbar>
    </div>
  );
};

const ContactsMenuList = ({ children, selectProps, ...props }) => {
  const { RenderActionsBar } = selectProps;
  return (
    <components.MenuList {...props} selectProps={selectProps}>
      <CustomScrollbar
        className='simple-scrollbar'
        autoHeight
        autoHeightMax={300}
        minimalThumbYSize={50}
        maximalThumbYSize={75}
      >
        {children}
      </CustomScrollbar>
      {RenderActionsBar?.()}
    </components.MenuList>
  );
};

const colourStyles = (disabled) => ({
  control: (styles) => ({
    ...styles,
    borderColor: 'transparent',
    outline: 'none',
    boxShadow: 'none',
    ':hover': { borderColor: 'transparent' },
    backgroundColor: 'transparent',
    cursor: 'text',
    minHeight: '40px',
  }),
  option: (styles) => ({
    ...styles,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: '#00122b',
    fontSize: '16px',
    ':active': { ...styles[':active'], backgroundColor: 'transparent' },
  }),
  placeholder: (styles) => ({ ...styles, color: 'rgba(0,18,43,0.6)' }),
  input: (styles) => ({ ...styles, margin: 0, padding: 0 }),
  indicatorsContainer: (styles) => ({ ...styles, backgroundColor: 'transparent' }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: 'transparent',
    borderRadius: 16,
    width: 'calc(100% - 4px)',
    margin: '2px',
    pointerEvents: disabled ? 'none' : 'all',
    ':hover': { backgroundColor: 'rgba(0, 148, 204, 0.04)' },
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    padding: '2px 6px',
    fontSize: 14,
    color: '#00122b',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    flex: 1,
    minWidth: 0,
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    borderRadius: '0 16px 16px 0',
    paddingLeft: 6,
    paddingRight: 6,
    color: '#0094cc',
    ':hover': { backgroundColor: 'rgba(0, 148, 204, 0.08)', color: '#0094cc' },
  }),
  menuList: (styles) => ({ ...styles, padding: 0, maxHeight: 'none', overflowY: 'hidden' }),
  menu: (styles) => ({ ...styles, boxShadow: 'none', border: 'none', margin: 0, top: 46 }),
  loadingIndicator: (styles) => ({ ...styles, position: 'absolute', top: '12px', right: '12px' }),
});

const ContactsSelect = ({
  loadOptions,
  value,
  onChange,
  placeholder,
  disabled = false,
  defaultOptions = false,
  selectRef,
  MultiValueLabel: CustomMultiValueLabel,
  Option: CustomOption,
  GroupHeading: CustomGroupHeading,
  noOptionsMessage = ({ inputValue: iv }) => (iv?.length > 0 ? 'No results' : null),
  ...rest
}) => (
  <div className='interaction-contacts-select position-relative flex-grow-1 w-100 mt-2'>
    <AsyncSelect
      ref={selectRef}
      isMulti
      isDisabled={disabled}
      defaultOptions={defaultOptions}
      cacheOptions={false}
      captureMenuScroll={false}
      menuShouldScrollIntoView={false}
      closeMenuOnSelect={false}
      loadOptions={loadOptions}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      classNamePrefix='react-custom-select-contacts'
      styles={colourStyles(disabled)}
      hideSelectedOptions={false}
      noOptionsMessage={noOptionsMessage}
      components={{
        ...(CustomMultiValueLabel && { MultiValueLabel: CustomMultiValueLabel }),
        ValueContainer: ScrolledValueContainer,
        MenuList: ContactsMenuList,
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
        ClearIndicator: () => null,
        ...(CustomOption && { Option: CustomOption }),
        ...(CustomGroupHeading && { GroupHeading: CustomGroupHeading }),
      }}
      {...rest}
    />
  </div>
);

export default ContactsSelect;
